// assetFrontend_1_bak/src/context/PermissionContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load permissions from localStorage on mount
  useEffect(() => {
    const loadPermissions = () => {
      try {
        const storedPermissions = localStorage.getItem('permissions');
        if (storedPermissions) {
          setPermissions(JSON.parse(storedPermissions));
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, []);

  // Check if user has a specific permission
  const hasPermission = (resource, action) => {
    if (!resource || !action) return false;

    return permissions.some(
      permission =>
        permission.resource === resource &&
        permission.action === action
    );
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionChecks) => {
    if (!permissionChecks || !Array.isArray(permissionChecks)) return false;

    return permissionChecks.some(check =>
      hasPermission(check.resource, check.action)
    );
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionChecks) => {
    if (!permissionChecks || !Array.isArray(permissionChecks)) return false;

    return permissionChecks.every(check =>
      hasPermission(check.resource, check.action)
    );
  };

  // Update permissions (called after login)
  const updatePermissions = (newPermissions) => {
    setPermissions(newPermissions || []);
    localStorage.setItem('permissions', JSON.stringify(newPermissions || []));
  };

  // Clear permissions (called on logout)
  const clearPermissions = () => {
    setPermissions([]);
    localStorage.removeItem('permissions');
  };

  // Get all permissions for a specific resource
  const getResourcePermissions = (resource) => {
    return permissions.filter(permission => permission.resource === resource);
  };

  // Check if user can view a resource (has view permission)
  const canView = (resource) => hasPermission(resource, 'view');

  // Check if user can create a resource (has create permission)
  const canCreate = (resource) => hasPermission(resource, 'create');

  // Check if user can edit a resource (has edit permission)
  const canEdit = (resource) => hasPermission(resource, 'edit');

  // Check if user can delete a resource (has delete permission)
  const canDelete = (resource) => hasPermission(resource, 'delete');

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    updatePermissions,
    clearPermissions,
    getResourcePermissions,
    canView,
    canCreate,
    canEdit,
    canDelete
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
